// SPDX-License-Identifier: AGPL-3.0-or-later

import type {RoomPoseSource} from '@app/features/voice_scene/hooks/usePositionSender';
import {ROOM_LIMITS} from '@fluxer/constants/src/RoomCharacters';
import {useEffect, useState} from 'react';

// My walking character, and the nearest seat nobody holds (null: none in the room).
export interface RoomWalkProbe extends RoomPoseSource {
	nearestFreeSeat(): {name: string; distance: number} | null;
}

export type WalkHint = 'sit' | 'crowded' | null;

export const SIT_HINT_DISTANCE_M = 1.2;
export const WALK_HINT_INTERVAL_MS = 200;

const sitHint = (walk: RoomWalkProbe): WalkHint => {
	const pose = walk.getPose();
	if (!pose || pose.anim === 'sit') return null;
	const seat = walk.nearestFreeSeat();
	return seat && seat.distance <= SIT_HINT_DISTANCE_M ? 'sit' : null;
};

// The walking hint: "sit" while I walk next to a free seat, "crowded" while the room is past the walking cap.
// walk: null while walking is off in this room; then never a hint and no timer. walking: my character walks; the sit
// hint's timer runs only then.
export function useWalkHint(walk: RoomWalkProbe | null, participantCount: number, walking = true): WalkHint {
	const crowded = participantCount > ROOM_LIMITS.posMaxParticipants;
	const [hint, setHint] = useState<WalkHint>(null);
	useEffect(() => {
		setHint(null);
		if (!walk || crowded || !walking) return;
		const interval = setInterval(() => setHint(sitHint(walk)), WALK_HINT_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [walk, crowded, walking]);
	if (!walk) return null;
	return crowded ? 'crowded' : hint;
}
